// EVA-279: the Discounting tab hydrates from two sources whose key casing
// differs, so the mapping lives here as a pure function rather than inline in
// the component — this is the whole reason the tab can be unit-tested.
//
//   ERB props   -> the mount controller runs them through humps `camelizeKeys`,
//                  which recurses into arrays: `input_method` -> `inputMethod`.
//   fetch/cable -> raw JSON straight off the wire, still snake_case.
//
// Reading only snake_case silently mis-hydrates every persisted row on page
// load: a Fixed Amount row would render as "% Purchase Price" and the next Save
// would rewrite 250,000 currency units as 250000 percent. Reading only
// camelCase breaks the save/broadcast round-trip instead. Both are accepted.

export const PERCENT_PURCHASE_PRICE = 'PERCENT_PURCHASE_PRICE'
export const FIXED_AMOUNT = 'FIXED_AMOUNT'
export const DEFAULT_DISCOUNTING_METHOD = 'MONTHLY_IN_ADVANCE'

// `??` rather than `||` throughout: an explicit false (applyVat) and an explicit
// "0" (amount) are real values and must not fall through to the default.
const pick = (row, camel, snake) => row[camel] ?? row[snake]

export function normalizeRow(row) {
  const inputMethod = pick(row, 'inputMethod', 'input_method') ?? PERCENT_PURCHASE_PRICE
  const amount = row.amount

  return {
    id: row.id ?? null,
    name: row.name ?? '',
    inputMethod,
    // Distinguishes a blank amount from an explicit zero, all the way down to a
    // nullable column.
    amount: amount === null || amount === undefined || amount === '' ? null : amount,
    applyVat: Boolean(pick(row, 'applyVat', 'apply_vat') ?? false)
  }
}

export function normalizeProps(props) {
  return {
    discountRatePercent: pick(props, 'discountRatePercent', 'discount_rate_percent') ?? null,
    discountingMethod:
      pick(props, 'discountingMethod', 'discounting_method') ?? DEFAULT_DISCOUNTING_METHOD,
    vatRatePercent: pick(props, 'vatRatePercent', 'vat_rate_percent') ?? null,
    rows: (pick(props, 'purchasersCosts', 'purchasers_costs') ?? []).map(normalizeRow)
  }
}

// Local keys for rows the server has not seen yet. React needs a stable key per
// row, and a brand new row has no id to build one from.
let localRowSeq = 0
export const nextLocalRowKey = () => `new-${(localRowSeq += 1)}`

// One definition of "a row the user has not filled in yet", shared by the tab's
// Add button and by the EVA-284 seed below — they must stay the same row, or the
// seeded one behaves differently from an added one on save.
export function blankDraftRow() {
  return {
    key: nextLocalRowKey(), id: null, name: '', inputMethod: PERCENT_PURCHASE_PRICE,
    amount: '', applyVat: false
  }
}

// EVA-284: an empty schedule presents one blank row rather than the empty-state
// message, so entering a cost takes typing and not a click first. It is an
// affordance, not a record — nothing is written until the user touches the tab.
// Once they do the row is persisted with everything else, so an unfilled
// Purchasers' Cost is now an ordinary state of the table rather than an
// abandoned edit; `PurchasersCost#populated?` is where that has to be filtered
// back out.
//
// The seed belongs to *opening* the tab, so only the two hydration paths that
// make up one page load take it — the mount initialiser and the EVA-283 state
// re-read. The cable handler deliberately does not: a broadcast is the server's
// truth arriving mid-session, and seeding there would answer another tab's
// deletion of the last row by growing a fresh blank one, which reads as the
// deletion undoing itself. The price of that choice, accepted: a broadcast
// caused by anything else — another tab moving only the discount rate — carries
// the whole tab, so it takes an unsaved seeded row off the screen with it. The
// row is back on the next load; an unsaved blank row has nothing to lose.
//
// `previousRows` is what keeps the state re-read from replacing the row the
// mount just put on screen. Both produce an identical blank row, but a new one
// carries a new key, and a new key remounts the <tr> — dropping the caret of a
// user who has clicked into the field but not yet typed, which is exactly the
// window in which the re-read is still allowed through (dirtyRef is false).
//
// A read-only viewer gets no seed: they cannot type, so a disabled blank row
// would read as a cost line whose name is missing. They keep
// `purchasers_costs.empty` — as does an owner who deletes the last row, until
// the next page load seeds it again.
export function withSeededRow(state, { readOnly, previousRows = [] }) {
  if (readOnly || state.rows.length > 0) return state

  // Same explicit pair as mergeSavedRowIds: an id is null on a new row and a
  // number on a saved one, and a missed guard here would silently mint a new key.
  const [candidate] = previousRows
  const reusable = previousRows.length === 1 && (candidate.id === null || candidate.id === undefined)

  return { ...state, rows: [reusable ? candidate : blankDraftRow()] }
}
